import { useMemo } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import * as actions from "../../_redux/private-notices/PrivateNoticesAction";
import {
  DeleteButton,
  ExportButton,
  FormButton,
  copy,
  deletionAlert,
  exportToExcel,
  exportToPDF,
  getAuthorities,
  noRecordsAlert,
  print,
  redirectToPages,
  resultAlert
} from "../../../../../_helpers/Helpers";
import { 
  Card, 
  CardBody, 
  CardHeader,
  CardHeaderToolbar,
  ModalProgressBar
} from "../../../../../_metronic/_partials/controls";
import { PrivateNoticesFilter } from "./private-notices-filter/PrivateNoticesFilter";
import { PrivateNoticesTable } from "./private-notices-table/PrivateNoticesTable";
import { usePrivateNoticesUIContext } from "./PrivateNoticesUIContext";

export function PrivateNoticesCard() {
  // PrivateNotices UI Context
  const privateNoticesUIContext = usePrivateNoticesUIContext();
  const privateNoticesUIProps = useMemo(() => {
    return {
      ids: privateNoticesUIContext.ids,
      setIds: privateNoticesUIContext.setIds,
      queryParams: privateNoticesUIContext.queryParams,
      openNewPrivateNoticePage: privateNoticesUIContext.openNewPrivateNoticePage,
    };
  }, [privateNoticesUIContext]);
    
  // Getting curret state of Private Notice list from store (Redux)
  const { currentState } = useSelector(
    (state) => ({ currentState: state.privateNotices }),
    shallowEqual
  );
  const { actionsLoading, isSuccess, type, authorities } = currentState;

  // Authority Parameters
  const { isExportationAuthorized, isAdditionAuthorized, isDeletionAuthorized } = getAuthorities(authorities);

  // Private Notices Redux state
  const dispatch = useDispatch();

  // Yetki ve Hata Kontrolleri
  if (isSuccess === false) return redirectToPages(type);

  /** Belli Bir Özel Duyuru Listesini Excele Aktarır */
  const exportPrivateNoticesToExcel = () => {
    dispatch(actions.getAllPrivateNotices(privateNoticesUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const rows = [];
      const widths = [25, 60, 60, 20, 30, 25, 15];
      const columns = ["Gönderen", "Tanımı", "Açıklaması", "Tarih", "Ek", "Gönderim Grubu", "Durum"];
      entities.forEach(entity => rows.push([entity.sender, entity.definition, entity.explanation, entity.createDate, entity.attachmentName, entity.receipentTypeName, entity.isSent ? "Gönderildi" : "Beklemede"]));
      const filename = privateNoticesUIProps.queryParams.term + " - " + (privateNoticesUIProps.queryParams.term + 1) + " Eğitim Öğretim Yılı Özel Duyuru Listesi";
      const sheetname = "Özel Duyuru Listesi"
      exportToExcel({ columns, rows, widths, filename, sheetname });
    });
  }

  /** Belli Bir Özel Duyuru Listesini PDF e Aktarır */
  const exportPrivateNoticesToPDF = () => {
    dispatch(actions.getAllPrivateNotices(privateNoticesUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const views = [ 
        {
          header: "Gönderen",
          rows: entities.map(entity => entity.sender)
        },
        {
          header: "Tanımı",
          rows: entities.map(entity => entity.definition)
        },
        {
          header: "Açıklaması",
          rows: entities.map(entity => entity.explanation)
        },
        {
          header: "Tarih",
          rows: entities.map(entity => entity.createDate)
        },
        {
          header: "Ek",
          rows: entities.map(entity => entity.attachmentName)
        },
        {
          header: "Gönderim Grubu",
          rows: entities.map(entity => entity.receipentTypeName)
        },
        {
          header: "Durum",
          rows: entities.map(entity => entity.isSent ? "Gönderildi" : "Beklemede")
        }
      ];
  
      const filename = privateNoticesUIProps.queryParams.term + " - " + (privateNoticesUIProps.queryParams.term + 1) + " Eğitim Öğretim Yılı Özel Duyuru Listesi";
      exportToPDF(views, filename);
    });
  }

  /** Belli Bir Özel Duyuru Listesini Yazdırma Penceresini Açar */
  const printPrivateNotices = () => {
    dispatch(actions.getAllPrivateNotices(privateNoticesUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const data = [];
      const properties = ["Gönderen", "Tanımı", "Açıklaması", "Tarih", "Ek", "Gönderim Grubu", "Durum"];
      const header = privateNoticesUIProps.queryParams.term + " - " + (privateNoticesUIProps.queryParams.term + 1) + " Eğitim Öğretim Yılı Özel Duyuru Listesi";

      entities.forEach(entity => {
        data.push({
          "Gönderen": entity.sender,
          "Tanımı": entity.definition,
          "Açıklaması": entity.explanation,
          "Tarih": entity.createDate,
          "Ek": entity.attachmentName ?? "",
          "Gönderim Grubu": entity.receipentTypeName,
          "Durum": entity.isSent ? "Gönderildi" : "Beklemede"
        });
      });

      print({ data, properties, header });
    });
  }

  /** Belli Bir Özel Duyuru Listesini Panoya Kopyalar */
  const copyPrivateNotices = () => {
    dispatch(actions.getAllPrivateNotices(privateNoticesUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const rows = [];
      const columns = ["Gönderen", "Tanımı", "Açıklaması", "Tarih", "Ek", "Gönderim Grubu", "Durum"];
      const header = privateNoticesUIProps.queryParams.term + " - " + (privateNoticesUIProps.queryParams.term + 1) + " Eğitim Öğretim Yılı Özel Duyuru Listesi";
      entities.forEach(entity => rows.push([entity.sender, entity.definition, entity.explanation, entity.createDate, entity.attachmentName ?? "", entity.receipentTypeName, entity.isSent ? "Gönderildi" : "Beklemede"]));
      copy({ header, columns, rows });
    });
  }

  /** Belli Bir Özel Duyuru Listesini Siler */
  const deletePrivateNotices = (ids) => {
    if (typeof ids === 'string') ids = ids.split(",");
  
    if (ids.length < 1) {
      noRecordsAlert();
      return;
    }  
    deletionAlert().then(result => {
      if (result.isConfirmed) dispatch(actions.deletePrivateNotices(ids)).then(result => onResult(result));
    });
  }

  const onResult = ({ isSuccess, type, message }) => {
    resultAlert(type, message);

    if (isSuccess) {
      setTimeout(() => {
        // clear selections list
        privateNoticesUIProps.setIds([]);
        // refresh list after deletion
        dispatch(actions.getPrivateNotices(privateNoticesUIProps.queryParams));        
      }, 2500);
    }
  };
  
  const title = <>Özel Duyuru Listesi{privateNoticesUIProps.ids.length > 0 ?
                  <> [Seçilen Kayıt Sayısı: <span style={{color:"red"}}>{privateNoticesUIProps.ids.length}</span>]</> :
                   ""}
                </>;

  return (
    <Card>
      {actionsLoading && <ModalProgressBar />}
      <CardHeader title={title}>
        {
          (isExportationAuthorized || isAdditionAuthorized || isDeletionAuthorized) &&
          <CardHeaderToolbar>
            {
              isExportationAuthorized &&
              <ExportButton
                exportToExcel={exportPrivateNoticesToExcel}
                exportToPDF={exportPrivateNoticesToPDF}
                print={printPrivateNotices}
                copy={copyPrivateNotices}
              />
            }
            {
              isAdditionAuthorized && 
              <FormButton 
                path="Navigation/Plus"
                title="Yeni Duyuru"
                method={privateNoticesUIProps.openNewPrivateNoticePage}
              />
            }
            {
              isDeletionAuthorized && 
              <DeleteButton
                title="Seçilenleri Sil"
                method={() => deletePrivateNotices(privateNoticesUIProps.ids)}
              />
            }
          </CardHeaderToolbar>
        }
      </CardHeader>
      <CardBody>
        <PrivateNoticesFilter />
        <PrivateNoticesTable
          deletePrivateNotices={deletePrivateNotices}
        />
      </CardBody>
    </Card>
  );
}